import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";
import { Button } from "./button";
import { computePageItems } from "./pagination-utils";

export interface PaginationProps {
  /** 0-based 当前页 */
  page: number;
  pageSize: number;
  /** 总条数 */
  total: number;
  /** 传入 0-based 目标页 */
  onPageChange: (page: number) => void;
  /** 传了才显示每页条数选择器 */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /** 显示"共 N 条";默认 true */
  showTotal?: boolean;
  /** 显示跳页输入框;默认 true */
  showJump?: boolean;
  /** 加载中等场景禁用全部翻页控件 */
  disabled?: boolean;
  siblings?: number;
  /** 窄容器紧凑档(如 292px 侧栏):只渲染 上一页 + 当前/总页 + 下一页,单行绝不折行。 */
  compact?: boolean;
  className?: string;
}

/**
 * 企业级共享分页器(#7):首/末页 + 上/下页 + 窗口页码(省略号)+ 跳页 + 可选每页条数。
 * 全平台 ~15 个服务端 page/size 列表页统一复用,替换各自手搓的 prev/next。
 * page 一律 0-based(对齐后端 page 参数);内部页码显示用 1-based。
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  showTotal = true,
  showJump = true,
  disabled = false,
  siblings = 1,
  compact = false,
  className,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const current = Math.min(Math.max(0, page), totalPages - 1) + 1; // 1-based, clamped
  const items = computePageItems(current, totalPages, siblings);
  const [jump, setJump] = React.useState("");

  const go = (target1: number) => {
    const clamped = Math.min(Math.max(1, target1), totalPages);
    onPageChange(clamped - 1);
  };

  const submitJump = () => {
    const n = Number(jump);
    if (Number.isFinite(n) && n >= 1) go(Math.floor(n));
    setJump("");
  };

  if (total <= 0) return null;

  if (compact) {
    return (
      <nav
        aria-label="分页"
        className={cn(
          "flex items-center justify-center gap-2 text-xs text-muted-foreground",
          className
        )}
      >
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          aria-label="上一页"
          disabled={disabled || current <= 1}
          onClick={() => go(current - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="tabular-nums">
          {current} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          aria-label="下一页"
          disabled={disabled || current >= totalPages}
          onClick={() => go(current + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </nav>
    );
  }

  return (
    <nav
      aria-label="分页"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground",
        className
      )}
    >
      {showTotal ? <span className="tabular-nums">共 {total} 条</span> : <span />}

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="首页"
          disabled={disabled || current <= 1}
          onClick={() => go(1)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="上一页"
          disabled={disabled || current <= 1}
          onClick={() => go(current - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {items.map((it, i) =>
          it === "ellipsis" ? (
            <span key={`e${i}`} className="px-1.5 text-muted-foreground/60 select-none">
              …
            </span>
          ) : (
            <Button
              key={it}
              variant={it === current ? "default" : "outline"}
              size="icon"
              className="h-8 min-w-8 tabular-nums"
              aria-label={`第 ${it} 页`}
              aria-current={it === current ? "page" : undefined}
              disabled={disabled}
              onClick={() => go(it)}
            >
              {it}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="下一页"
          disabled={disabled || current >= totalPages}
          onClick={() => go(current + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="末页"
          disabled={disabled || current >= totalPages}
          onClick={() => go(totalPages)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
          <select
            aria-label="每页条数"
            className="h-8 rounded-md border border-input bg-background px-2 text-xs disabled:opacity-50"
            value={pageSize}
            disabled={disabled}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>
                {s} 条/页
              </option>
            ))}
          </select>
        )}
        {showJump && totalPages > 1 && (
          <span className="flex items-center gap-1">
            跳至
            <input
              aria-label="跳至页码"
              inputMode="numeric"
              className="h-8 w-12 rounded-md border border-input bg-background px-2 text-center text-xs disabled:opacity-50"
              disabled={disabled}
              value={jump}
              onChange={(e) => setJump(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitJump();
              }}
              onBlur={submitJump}
            />
            页
          </span>
        )}
      </div>
    </nav>
  );
}
